import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import UserContext from "../../components/User_Context.js";
import * as menu from "../../assets/menus/menu.js";
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import { useTranslation } from "react-i18next";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { LisLabels } from "../_label/lis_labels/lis_labels.js";
import Choices from "../../components/Choices.js";
import LisTableHeader from "../../components/lis/LisTableHeader.js";
import LisMainTable from "../../components/lis/LisMainTable.js";
import Select from "react-select";

const LisAlertAssignmentScreen = () => {
  const { currentUser } = useContext(UserContext);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const MySwal = withReactContent(Swal);

  const i18n = menu.getI18n();
  i18n.addResourceBundle("it", "lisLabels", LisLabels.it, true, true);
  i18n.addResourceBundle("en", "lisLabels", LisLabels.en, true, true);

  /**
   * Get deadline urgency styling based on days since test
   * No background colors or borders, just text indicator
   */
  const getDeadlineStyle = (days) => {
    // No special styling, keep column uniform
    return {};
  };

  /**
   * Get deadline icon based on days since test
   */
  const getDeadlineIcon = (days) => {
    if (days === null || days === undefined) return '📅';
    
    if (days > 7) return '🔴';
    if (days > 3) return '⚠️';
    return '✅';
  };

  const [tableData, setTableData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [assignmentStatus, setAssignmentStatus] = useState("ALL"); // Default: show all
  const [activeFilters, setActiveFilters] = useState({
    fromDate: "",
    toDate: "",
    testCode: "",
    materialName: "",
    microorganismName: "",
  });

  const [filterInputs, setFilterInputs] = useState({
    fromDate: "",
    toDate: "",
    testCode: "",
    materialName: "",
    microorganismName: "",
  });

  // Modal states for assignment
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [isAssigning, setIsAssigning] = useState(false);
  
  // Counters for assigned/unassigned alerts
  const [counters, setCounters] = useState({
    toBeAssigned: 0,
    assigned: 0,
    total: 0
  });
  
  // Side panel for alert details
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [selectedAlertDetails, setSelectedAlertDetails] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  
  // Collapsible sections state (true = open, false = closed)
  const [openSections, setOpenSections] = useState({
    patient: true,
    test: true,
    microorganisms: true,
    antibiotics: true,
    assignment: true,
  });
  
  // Toggle section open/close
  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Uniform sizing consistent with DMS table toolbar
  const toolbarItemWidthPx = 130;
  const compactHeightPx = 42;
  const uniformControlStyle = {
    height: compactHeightPx,
    minHeight: compactHeightPx,
  };

  const assignmentStatusOptions = [
    { value: "TO_BE_ASSIGNED", label: "🔴 Da Assegnare" },
    { value: "ASSIGNED", label: "✅ Assegnati" },
    { value: "ALL", label: "📋 Tutti" },
  ];

  useEffect(() => {
    // Set breadcrumb
    const path = [
      { link: "/dashboard", name: "Home" },
      { link: "/admin", name: "Admin" },
      { link: "/admin/lis_alert_assignment", name: "LIS Alert Assignment" },
    ];
    breadcrumb.set(path);

    fetchTableData({ page: 1, pageSizeParam: 100, ...activeFilters });

    // Write menu to make it visible
    menu.write({ user: currentUser() });
  }, []);

  const fetchTableData = async (params = {}) => {
    const {
      page = 1,
      pageSizeParam = 100,
      assignmentStatusParam = assignmentStatus,
      fromDate = activeFilters.fromDate,
      toDate = activeFilters.toDate,
      testCode = activeFilters.testCode,
      materialName = activeFilters.materialName,
      microorganismName = activeFilters.microorganismName,
    } = params || {};

    try {
      setIsLoading(true);
      const effectivePageSize = pageSizeParam ?? pagination?.pageSize ?? 10;
      const userBusinessUnit = currentUser()?.BusinessUnitId ?? null;

      const response = await api.sendMessage(`admin/lis/infectionAlerts`, {
        user: currentUser(),
        body: {
          page,
          pageSize: effectivePageSize,
          businessUnit: userBusinessUnit,
          assignmentStatus: assignmentStatusParam,
          // Search filters
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
          testCode: testCode || undefined,
          materialName: materialName || undefined,
          microorganismName: microorganismName || undefined,
        },
      });
      if (response.success) {
        setTableData(response.data);
        setHeaders(response.headers || []);
        setPagination(response.pagination || null);
        
        // Update counters
        if (response.counters) {
          setCounters(response.counters);
        }
      }
    } catch (error) {
      console.error("Error fetching table data:", error);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch table data",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    const filters = {
      fromDate: filterInputs.fromDate,
      toDate: filterInputs.toDate,
      testCode: filterInputs.testCode,
      materialName: filterInputs.materialName,
      microorganismName: filterInputs.microorganismName,
    };

    setActiveFilters(filters);
    fetchTableData({
      page: 1,
      pageSizeParam: pagination?.pageSize ?? 100,
      ...filters,
    });
  };

  const handleResetFilters = () => {
    const emptyFilters = {
      fromDate: "",
      toDate: "",
      testCode: "",
      materialName: "",
      microorganismName: "",
    };

    setFilterInputs(emptyFilters);
    setActiveFilters(emptyFilters);
    fetchTableData({
      page: 1,
      pageSizeParam: pagination?.pageSize ?? 100,
      ...emptyFilters,
    });
  };

  /**
   * Open assignment modal for a specific alert
   * Fetches assignable users from the same business unit as the current supervisor
   */
  const openAssignModal = async (alert) => {
    try {
      setSelectedAlert(alert);
      setSelectedUserId("");
      setShowAssignModal(true);

      // Fetch assignable users (same business unit as supervisor)
      const response = await api.sendMessage(
        "admin/lis/infectionAlerts/assignableUsers",
        {
          user: currentUser(),
          body: {},
        }
      );

      if (response.success) {
        // Transform users to add FullName + BusinessUnit for Choices component
        const transformedUsers = (response.users || []).map((user) => ({
          ...user,
          Code: user.Id, // Choices uses "Code" as value
          FullName: user.BusinessUnitName 
            ? `${user.Name} ${user.LastName} (${user.BusinessUnitName})`  // Show BU in dropdown
            : `${user.Name} ${user.LastName}`, // Fallback without BU
          DisplayName: `${user.Name} ${user.LastName}`,  // Name without BU for later use
          BULevel: user.BusinessUnitLevel || 0,  // For sorting
        }));
        
        // Sort by BU Level first, then by name
        transformedUsers.sort((a, b) => {
          if (a.BULevel !== b.BULevel) {
            return a.BULevel - b.BULevel;  // Level 0, 1, 2...
          }
          return a.DisplayName.localeCompare(b.DisplayName);
        });
        
        console.log(
          "✅ Assignable users loaded (sorted by BU hierarchy):",
          transformedUsers.length,
          transformedUsers
        );
        setAssignableUsers(transformedUsers);
      } else {
        throw new Error(response.message || "Failed to fetch assignable users");
      }
    } catch (error) {
      console.error("Error fetching assignable users:", error);
      MySwal.fire({
        icon: "error",
        title: "Errore",
        text: "Impossibile recuperare gli utenti assegnabili",
      });
      closeAssignModal();
    }
  };

  /**
   * Close assignment modal and reset state
   */
  const closeAssignModal = () => {
    setShowAssignModal(false);
    setSelectedAlert(null);
    setSelectedUserId("");
    setAssignableUsers([]);
  };

  /**
   * Open details panel for a specific alert
   * Fetches complete test data including all microorganisms and antibiotics
   */
  const openDetailsPanel = async (alert) => {
    try {
      setIsLoadingDetails(true);
      setShowDetailsPanel(true);
      setSelectedAlertDetails(null);

      // Fetch complete test details
      const response = await api.sendMessage(
        "admin/lis/infectionAlerts/details",
        {
          user: currentUser(),
          body: {
            testId: alert.Test_Id,
          },
        }
      );

      if (response.success) {
        setSelectedAlertDetails(response.data);
      } else {
        throw new Error(response.message || "Failed to fetch alert details");
      }
    } catch (error) {
      console.error("Error fetching alert details:", error);
      MySwal.fire({
        icon: "error",
        title: "Errore",
        text: "Impossibile recuperare i dettagli dell'alert",
      });
      closeDetailsPanel();
    } finally {
      setIsLoadingDetails(false);
    }
  };

  /**
   * Close details panel
   */
  const closeDetailsPanel = () => {
    setShowDetailsPanel(false);
    setSelectedAlertDetails(null);
  };

  /**
   * Handle assignment of alert to selected user
   * Shows confirmation dialog before assigning
   */
  const handleAssign = async () => {
    if (!selectedUserId) {
      MySwal.fire({
        icon: "warning",
        title: "Attenzione",
        text: "Seleziona un utente prima di assegnare",
      });
      return;
    }

    // Find selected user name for confirmation message (use Code which is the same as Id)
    const selectedUserObj = assignableUsers.find(
      (u) => u.Code === selectedUserId
    );
    const userName = selectedUserObj
      ? selectedUserObj.DisplayName || selectedUserObj.FullName  // Use DisplayName (without BU) for confirmation
      : "questo utente";

    // Show confirmation dialog
    const confirmResult = await MySwal.fire({
      icon: "question",
      title: "Conferma Assegnazione",
      text: `Sei sicuro di voler assegnare questo alert a ${userName}?`,
      showCancelButton: true,
      confirmButtonText: "Sì, assegna",
      cancelButtonText: "Annulla",
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      setIsAssigning(true);

      // Show loading modal
      MySwal.fire({
        title: "Assegnazione in corso...",
        text: "Attendere prego",
        allowOutsideClick: false,
        showConfirmButton: false,
        willOpen: () => {
          Swal.showLoading();
        },
      });

      const response = await api.sendMessage(
        "admin/lis/infectionAlerts/assign",
        {
          user: currentUser(),
          body: {
            testId: selectedAlert.Test_Id,
            assignedToUserId: selectedUserId, // This is the Code, which equals the Id
          },
        }
      );

      if (response.success) {
        // Show success message
        MySwal.fire({
          icon: "success",
          title: "Successo",
          text: "Alert assegnato con successo",
          timer: 2000,
          showConfirmButton: false,
        });

        // Close modal and refresh table
        closeAssignModal();
        fetchTableData({
          page: pagination?.currentPage ?? 1,
          pageSizeParam: pagination?.pageSize ?? 100,
          ...activeFilters,
        });
      } else {
        throw new Error(response.message || "Errore durante l'assegnazione");
      }
    } catch (error) {
      console.error("Error assigning alert:", error);
      MySwal.fire({
        icon: "error",
        title: "Errore",
        text: error.message || "Impossibile assegnare l'alert",
      });
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
      
      <div className="page-titles">
        <ol className="breadcrumb"></ol>
      </div>
      <div className="container-fluid">
        <div className="card-body p-0 bg-white">
          <div className="table-responsive active-projects task-table">
            <div className="tbl-caption h-auto ">
              <div className="row">
                <div className="col-12">
                  <div className="d-flex justify-content-between align-items-center">
                    <h3 className="mb-0">
                      <i className="fas fa-address-book me-2"></i>
                      {t("lisAlertAssignment", { ns: "lisLabels" })}
                    </h3>

                    <div>
                      <label className="form-label small text-muted mb-1">
                        {t("filterStatus", { ns: "lisLabels" })}
                      </label>
                      <Select
                        options={assignmentStatusOptions}
                        value={assignmentStatusOptions.find(
                          (o) => o.value === assignmentStatus
                        )}
                        onChange={(opt) => {
                          const newStatus = opt?.value;
                          setAssignmentStatus(newStatus);
                          fetchTableData({
                            page: 1,
                            pageSizeParam: pagination?.pageSize ?? 100,
                            assignmentStatusParam: newStatus,
                          });
                        }}
                        styles={{
                          container: (base) => ({ ...base, minWidth: "200px" }),
                        }}
                        classNamePrefix="react-select"
                        isSearchable={false}
                      />
                    </div>
                  </div>
                  <p className="text-muted">
                    {t("lisAlertAssignmentDescription", { ns: "lisLabels" })}
                  </p>
                </div>
              </div>
              <LisTableHeader
                filtersConfig={[
                  {
                    key: "testCode",
                    label: t("testCodeLabel", { ns: "lisLabels" }),
                    type: "text",
                    placeholder: t("testCodePlaceholder", { ns: "lisLabels" }),
                  },
                  {
                    key: "materialName",
                    label: t("materialLabel", { ns: "lisLabels" }),
                    type: "text",
                    placeholder: t("materialPlaceholder", { ns: "lisLabels" }),
                  },
                  {
                    key: "microorganismName",
                    label: t("microorganismLabel", { ns: "lisLabels" }),
                    type: "text",
                    placeholder: t("microorganismPlaceholder", { ns: "lisLabels" }),
                  },
                  { key: "fromDate", label: t("fromDateLabel", { ns: "lisLabels" }), type: "date" },
                  { key: "toDate", label: t("toDateLabel", { ns: "lisLabels" }), type: "date" },
                ]}
                values={filterInputs}
                onChange={(key, value) =>
                  setFilterInputs({ ...filterInputs, [key]: value })
                }
                onSearch={handleSearch}
                onReset={handleResetFilters}
                isLoading={isLoading}
                uniformControlStyle={uniformControlStyle}
                columnClass="col-md-2"
              />
              <div className="card">
                <div className="card-body">
                  <LisMainTable
                    headers={headers}
                    rows={tableData}
                    isLoading={isLoading}
                    onRowClick={openDetailsPanel}
                    getRowKey={(row, idx) => row.Test_Id || idx}
                    getRowStyle={(row) => {
                      const isAssigned =
                        row.AssignedTo_Name != null &&
                        row.AssignedTo_Name !== "";
                      return {
                        cursor: "pointer",
                        backgroundColor: isAssigned ? "#f8f9fa" : "inherit",
                      };
                    }}
                    getRowTitle={() => "Clicca per visualizzare dettagli"}
                    noDataMessage="Nessun alert trovato"
                    renderCell={({ row, header, value }) => {
                      if (header.key === "Test_Date") {
                        const days = row.Days_Since_Test;
                        const icon = getDeadlineIcon(days);
                        if (value) {
                          try {
                            const date = new Date(value);
                            const formattedDate = date.toLocaleDateString(
                              "it-IT",
                              { year: "numeric", month: "2-digit", day: "2-digit" }
                            );
                            return (
                              <div className="d-flex align-items-center gap-2">
                                <span>{icon}</span>
                                <div>
                                  <div>{formattedDate}</div>
                                  {days !== null && days !== undefined && (
                                    <small className="text-muted">
                                      {days === 0
                                        ? "Oggi"
                                        : days === 1
                                        ? "1 giorno fa"
                                        : `${days} giorni fa`}
                                    </small>
                                  )}
                                </div>
                              </div>
                            );
                          } catch (e) {
                            // fall through
                          }
                        }
                      }
                      if (header.key === "AssignedOn") {
                        if (value) {
                          try {
                            const date = new Date(value);
                            return date.toLocaleDateString("it-IT", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            });
                          } catch (e) {
                            // fall through
                          }
                        }
                      }
                      if (header.key === "AssignedTo_Name" && value) {
                        return (
                          <span className="badge bg-success">
                            <i className="fa-solid fa-check me-1"></i>
                            {value}
                          </span>
                        );
                      }
                      if (header.key === "Completion_Status") {
                        const isAssigned = row.AssignedTo_Name != null && row.AssignedTo_Name !== "";
                        if (!isAssigned) return "-";
                        
                        let badgeClass = "bg-light text-dark";
                        let label = "Da iniziare";
                        let icon = "fa-regular fa-circle";

                        const status = value?.toLowerCase();
                        if (status === "saved") {
                          badgeClass = "bg-warning text-dark";
                          label = "In bozza";
                          icon = "fa-solid fa-pen-to-square";
                        } else if (status === "compiled") {
                          badgeClass = "bg-info text-white";
                          label = "Compilata";
                          icon = "fa-solid fa-paper-plane";
                        } else if (status === "approved") {
                          badgeClass = "bg-success text-white";
                          label = "Approvata";
                          icon = "fa-solid fa-check-double";
                        }

                        return (
                          <span className={`badge ${badgeClass}`}>
                            <i className={`${icon} me-1`}></i>
                            {label}
                          </span>
                        );
                      }
                      return value !== undefined && value !== null ? String(value) : "-";
                    }}
                    renderActions={(row) => {
                      const isAssigned =
                        row.AssignedTo_Name != null &&
                        row.AssignedTo_Name !== "";
                      const hasChecklist = row.Completion_Id != null;
                      
                      return (
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => openDetailsPanel(row)}
                            title="Visualizza dettagli completi"
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                          {hasChecklist && (
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => {
                                const formId = row.Checklist_Form_Id || "2B343585-DDCD-4E36-B53C-16C60043B982";
                                navigate(`/form/compile/${formId}/${row.Completion_Id}`);
                              }}
                              title="Apri Checklist ICA"
                            >
                              <i className="fa-solid fa-file-signature"></i>
                            </button>
                          )}
                          {!isAssigned && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => openAssignModal(row)}
                              title="Assegna alert"
                            >
                              <i className="fa-solid fa-user-plus"></i>
                            </button>
                          )}
                        </div>
                      );
                    }}
                    pagination={pagination}
                    onPageChange={(pageNum) =>
                      fetchTableData({
                        page: pageNum,
                        pageSizeParam: pagination?.pageSize,
                        ...activeFilters,
                      })
                    }
                    maxHeight={450}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Assignment Modal */}
      {showAssignModal && selectedAlert && (
        <div
          id="lis-alert-assign-modal"
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1050,
          }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fa-solid fa-user-plus me-2"></i>
                  Assegna Alert
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeAssignModal}
                  disabled={isAssigning}
                ></button>
              </div>

              <div className="modal-body">
                {/* Patient Name */}
                <div className="alert alert-info mb-3">
                  <i className="fa-solid fa-user-injured me-2"></i>
                  <strong>Paziente:</strong> {(selectedAlert.Patient_Name || "").substring(0, 3)}{" "}
                  {(selectedAlert.Patient_LastName || "").substring(0, 3)}
                  <br />
                  <i className="fa-solid fa-bacterium me-2 mt-1"></i>
                  <strong>Microrganismo:</strong>{" "}
                  {selectedAlert.Microorganism_Name || "N/A"}
                </div>

                {/* User Selection Dropdown */}
                <div className="mb-3">
                  <label className="form-label">
                    Seleziona utente a cui assegnare{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <Choices
                    voc={assignableUsers}
                    value="Code"
                    text="FullName"
                    className="inputStyle blackText bg-white"
                    selected={selectedUserId ? [{ Code: selectedUserId }] : []}
                    onChange={(e) => {
                      const selectedCode = e.selected?.[0]?.Code || "";
                      setSelectedUserId(selectedCode);
                    }}
                    disabled={isAssigning}
                    placeholder="Cerca utente..."
                    dropDownParent="root"
                  />
                </div>

                <div className="alert alert-warning small">
                  <i className="fa-solid fa-info-circle me-1"></i>
                  Verranno mostrati solo gli utenti della tua stessa Business
                  Unit.
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeAssignModal}
                  disabled={isAssigning}
                >
                  <i className="fa-solid fa-times me-1"></i>
                  Annulla
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAssign}
                  disabled={isAssigning || !selectedUserId}
                >
                  <i className="fa-solid fa-check me-1"></i>
                  Assegna
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Details Side Panel */}
      {showDetailsPanel && (
        <>
          {/* Backdrop overlay */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.3)',
              zIndex: 1040,
            }}
            onClick={closeDetailsPanel}
          ></div>
          
          {/* Side panel */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '600px',
              maxWidth: '90vw',
              backgroundColor: 'white',
              boxShadow: '-2px 0 10px rgba(0,0,0,0.1)',
              zIndex: 1050,
              overflowY: 'auto',
              animation: 'slideInRight 0.3s ease-out',
            }}
          >
            {/* Panel Header */}
            <div 
              className="bg-primary text-white p-3 d-flex justify-content-between align-items-center sticky-top"
              style={{ top: 0 }}
            >
              <h5 className="mb-0">
                <i className="fa-solid fa-flask me-2"></i>
                Dettagli Alert LIS
              </h5>
              <button
                className="btn btn-sm btn-light"
                onClick={closeDetailsPanel}
              >
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            {/* Panel Content */}
            <div className="p-3">
              {isLoadingDetails ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Caricamento...</span>
                  </div>
                  <p className="text-muted mt-2">Caricamento dettagli...</p>
                </div>
              ) : selectedAlertDetails ? (
                <>
                  {/* Patient Info */}
                  <div className="card mb-3">
                    <div 
                      className="card-header bg-light d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleSection('patient')}
                    >
                      <h6 className="mb-0">
                        <i className="fa-solid fa-user-injured me-2"></i>
                        Informazioni Paziente
                      </h6>
                      <i className={`fa-solid fa-chevron-${openSections.patient ? 'up' : 'down'}`}></i>
                    </div>
                    {openSections.patient && (
                      <div className="card-body">
                        <div className="row g-2">
                          <div className="col-6">
                            <small className="text-muted d-block">Nome</small>
                            <strong>{(selectedAlertDetails.patient?.name || 'N/A').substring(0, 3)}</strong>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">Cognome</small>
                            <strong>{(selectedAlertDetails.patient?.lastName || 'N/A').substring(0, 3)}</strong>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">Sesso</small>
                            <strong>{selectedAlertDetails.patient?.sex || 'N/A'}</strong>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">Unità Operativa</small>
                            <strong>{selectedAlertDetails.businessUnit?.name || 'N/A'}</strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Test Info */}
                  <div className="card mb-3">
                    <div 
                      className="card-header bg-light d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleSection('test')}
                    >
                      <h6 className="mb-0">
                        <i className="fa-solid fa-vial me-2"></i>
                        Informazioni Esame
                      </h6>
                      <i className={`fa-solid fa-chevron-${openSections.test ? 'up' : 'down'}`}></i>
                    </div>
                    {openSections.test && (
                      <div className="card-body">
                        <div className="row g-2">
                          <div className="col-6">
                            <small className="text-muted d-block">Data Esame</small>
                            <strong>
                              {selectedAlertDetails.test?.date 
                                ? new Date(selectedAlertDetails.test.date).toLocaleDateString('it-IT')
                                : 'N/A'}
                            </strong>
                          </div>
                          <div className="col-6">
                            <small className="text-muted d-block">Codice Esame</small>
                            <strong>{selectedAlertDetails.test?.code || 'N/A'}</strong>
                          </div>
                          <div className="col-12">
                            <small className="text-muted d-block">Materiale</small>
                            <strong>{selectedAlertDetails.material?.description || 'N/A'}</strong>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Microorganisms */}
                  <div className="card mb-3">
                    <div 
                      className="card-header bg-light d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleSection('microorganisms')}
                    >
                      <h6 className="mb-0">
                        <i className="fa-solid fa-bacterium me-2"></i>
                        Microrganismi Isolati ({selectedAlertDetails.microorganisms?.length || 0})
                      </h6>
                      <i className={`fa-solid fa-chevron-${openSections.microorganisms ? 'up' : 'down'}`}></i>
                    </div>
                    {openSections.microorganisms && (
                      <div className="card-body">
                        {selectedAlertDetails.microorganisms && selectedAlertDetails.microorganisms.length > 0 ? (
                          <div className="list-group list-group-flush">
                            {selectedAlertDetails.microorganisms.map((mo, idx) => (
                              <div key={idx} className="list-group-item px-0">
                                <div className="d-flex align-items-center">
                                  <span className="badge bg-primary me-2">{idx + 1}</span>
                                  <div>
                                    <strong>{mo.name}</strong>
                                    <br />
                                    <small className="text-muted">Codice: {mo.code}</small>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted mb-0">Nessun microrganismo trovato</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Antibiotics / Antibiogram */}
                  <div className="card mb-3">
                    <div 
                      className="card-header bg-light d-flex justify-content-between align-items-center"
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleSection('antibiotics')}
                    >
                      <h6 className="mb-0">
                        <i className="fa-solid fa-pills me-2"></i>
                        Antibiogramma ({selectedAlertDetails.antibiotics?.length || 0})
                      </h6>
                      <i className={`fa-solid fa-chevron-${openSections.antibiotics ? 'up' : 'down'}`}></i>
                    </div>
                    {openSections.antibiotics && (
                      <div className="card-body">
                      {selectedAlertDetails.antibiotics && selectedAlertDetails.antibiotics.length > 0 ? (
                        (() => {
                          // Group antibiotics by microorganism
                          const grouped = selectedAlertDetails.antibiotics.reduce((acc, ab) => {
                            const moName = ab.microorganismName || 'Altro';
                            if (!acc[moName]) acc[moName] = [];
                            acc[moName].push(ab);
                            return acc;
                          }, {});

                          return Object.entries(grouped).map(([moName, abs], groupIdx) => (
                            <div key={groupIdx} className={groupIdx > 0 ? "mt-4" : ""}>
                              <h6 className="text-primary border-bottom pb-2 mb-3">
                                <i className="fa-solid fa-microscope me-2"></i>
                                {moName}
                              </h6>
                              <div className="table-responsive">
                                <table className="table table-sm table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th>Antibiotico</th>
                                      <th className="text-center">S/R/I</th>
                                      <th className="text-center">MIC</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {abs.map((ab, idx) => (
                                      <tr key={idx}>
                                        <td>
                                          <strong>{ab.name}</strong>
                                          <br />
                                          <small className="text-muted">Codice: {ab.code}</small>
                                        </td>
                                        <td className="text-center">
                                          <span
                                            className={`badge ${
                                              ab.sensitivity === 'S'
                                                ? 'bg-success'
                                                : ab.sensitivity === 'R'
                                                ? 'bg-danger'
                                                : ab.sensitivity === 'I'
                                                ? 'bg-warning text-dark'
                                                : 'bg-secondary'
                                            }`}
                                          >
                                            {ab.sensitivity || '-'}
                                          </span>
                                        </td>
                                        <td className="text-center" style={{ fontFamily: 'monospace' }}>
                                          {ab.mic || '-'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ));
                        })()
                      ) : (
                        <p className="text-muted mb-0">Nessun dato antibiogramma disponibile</p>
                      )}
                    </div>
                    )}
                  </div>

                  {/* Assignment Info (if assigned) */}
                  {selectedAlertDetails.assignment && (
                    <div className="card mb-3">
                      <div 
                        className="card-header bg-light d-flex justify-content-between align-items-center"
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleSection('assignment')}
                      >
                        <h6 className="mb-0">
                          <i className="fa-solid fa-user-check me-2"></i>
                          Informazioni Assegnazione
                        </h6>
                        <i className={`fa-solid fa-chevron-${openSections.assignment ? 'up' : 'down'}`}></i>
                      </div>
                      {openSections.assignment && (
                        <div className="card-body">
                          <div className="row g-2">
                            <div className="col-6">
                              <small className="text-muted d-block">Assegnato a</small>
                              <strong>{selectedAlertDetails.assignment.assignedTo || 'N/A'}</strong>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Data Assegnazione</small>
                              <strong>
                                {selectedAlertDetails.assignment.assignedOn
                                  ? new Date(selectedAlertDetails.assignment.assignedOn).toLocaleDateString('it-IT', {
                                      year: 'numeric',
                                      month: '2-digit',
                                      day: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : 'N/A'}
                              </strong>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="alert alert-warning">
                  <i className="fa-solid fa-exclamation-triangle me-2"></i>
                  Nessun dettaglio disponibile
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default LisAlertAssignmentScreen;
